import type { FC } from 'react';
import CloudDownloadOutlined from '@mui/icons-material/CloudDownloadOutlined';
import RotateRightRounded from '@mui/icons-material/RotateRightRounded';
import { LoadingButton } from '@mui/lab';
import { Dialog, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import type { TFunction } from 'i18next';

import { NO_BACKOFF_RESULT, ReportOrderStatus } from 'src/constants/report';
import { getLanguageAbbreviation } from 'src/helper/helper';
import ModalCloseHeader from 'src/components/common/Modal/ModalCloseHeader';

import { styles } from './index.styles';

export const REPORT_TABLE_ORDER_BUTTON_TEST_ID = 'report-table-order-button';
export const REPORT_TABLE_RETRY_BUTTON_TEST_ID = 'report-table-retry-button';

interface OrderButtonProps {
  handleOrderReport: () => void;
  handleReportStatusRefresh: () => void;
  modalCloseCallback: () => void;
  orderStatus: number;
  language: number;
  t: TFunction;
}

const OrderButton: FC<OrderButtonProps> = ({
  handleOrderReport,
  handleReportStatusRefresh,
  modalCloseCallback,
  orderStatus,
  language,
  t,
}) =>
  ReportOrderStatus.CREATED !== orderStatus &&
  orderStatus !== NO_BACKOFF_RESULT ? (
    <>
      <Tooltip title="Report has to be generated first.">
        <span>
          <LoadingButton
            variant="squareCta"
            color="secondary"
            sx={styles.button}
            onClick={handleOrderReport}
            disabled={orderStatus === ReportOrderStatus.ERROR}
            loading={
              orderStatus === ReportOrderStatus.IN_PRODUCTION ||
              orderStatus === ReportOrderStatus.ORDERED
            }
            startIcon={<CloudDownloadOutlined />}
            data-test-id={REPORT_TABLE_ORDER_BUTTON_TEST_ID}
          >
            {getLanguageAbbreviation(language)?.toUpperCase()}
          </LoadingButton>
        </span>
      </Tooltip>
      <Dialog
        open={orderStatus === ReportOrderStatus.ERROR}
        onClose={modalCloseCallback}
      >
        <ModalCloseHeader onClose={modalCloseCallback} />
        <DialogTitle variant="h4" mb={0.5}>
          {t('common:error')}
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          {t('report:reportStatus.orderError')}
        </DialogContent>
      </Dialog>
    </>
  ) : orderStatus === NO_BACKOFF_RESULT ? (
    <LoadingButton
      variant="squareCta"
      color="secondary"
      sx={styles.button}
      onClick={handleReportStatusRefresh}
      startIcon={<RotateRightRounded />}
      data-test-id={REPORT_TABLE_RETRY_BUTTON_TEST_ID}
    >
      {getLanguageAbbreviation(language)?.toUpperCase()}
    </LoadingButton>
  ) : null;

export default OrderButton;
